/**
 * <product-carousel>
 * Versión de una sola colección de FeaturedCollections:
 * mismo keen-slider (v6), mismos breakpoints/espaciados, mismo plugin `navigation`
 * y las flechas externas reflejan el estado arrow--disabled de las internas.
 */
if (!customElements.get('product-carousel')) {
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.collection-content');
      this.sliderEl = this.querySelector('.featured-collections-slider');
      this.leftArrow = this.querySelector('.featured-collections__body > .icon-arrow.arrow--left');
      this.rightArrow = this.querySelector('.featured-collections__body > .icon-arrow.arrow--right');

      if (!this.sliderEl || typeof KeenSlider === 'undefined') return;

      const mobile = parseFloat(this.dataset.perViewMobile) || 2;
      const desktop = parseFloat(this.dataset.perViewDesktop) || 4;
      const tablet = Math.max(mobile, Math.min(desktop, 3));

      // Plugin de flechas del tema (el mismo que usa featured-collections.js)
      const plugins = typeof navigation === 'function' ? [navigation] : [];

      this.drag = false;
      this.slider = new KeenSlider(this.sliderEl, {
        dragChecked: () => { this.drag = true; },
        dragEnded: () => { setTimeout(() => (this.drag = false), 0); },
        slideChanged: () => this.syncArrows(),
        updated: () => this.syncArrows(),
        slides: { perView: desktop, spacing: 24 },
        breakpoints: {
          '(max-width: 749px)': { slides: { perView: mobile, spacing: 16 } },
          '(min-width: 750px)': { slides: { perView: tablet, spacing: 24 } },
          '(min-width: 990px)': { slides: { perView: desktop, spacing: 24 } },
        },
      }, plugins);

      // Evita abrir el producto al soltar un arrastre
      this.onSliderClick = (e) => {
        if (this.drag) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      this.sliderEl.addEventListener('click', this.onSliderClick, true);

      // Flechas externas
      this.bindArrow(this.leftArrow, 'left');
      this.bindArrow(this.rightArrow, 'right');

      // Cuando el plugin cambia arrow--disabled en las flechas internas, lo copiamos
      if (this.content) {
        this.observer = new MutationObserver(() => this.syncArrows());
        this.observer.observe(this.content, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      this.syncArrows();
    }

    innerArrow(dir) {
      return this.content ? this.content.querySelector(`.arrow--${dir}`) : null;
    }

    move(dir) {
      const inner = this.innerArrow(dir);
      if (inner) inner.click();
      else if (this.slider) dir === 'left' ? this.slider.prev() : this.slider.next();
    }

    bindArrow(arrow, dir) {
      if (!arrow) return;
      arrow.addEventListener('click', () => this.move(dir));
      arrow.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.move(dir);
        }
      });
    }

    syncArrows() {
      if (!this.slider || !this.leftArrow || !this.rightArrow) return;

      const innerLeft = this.innerArrow('left');
      const innerRight = this.innerArrow('right');
      let leftDisabled;
      let rightDisabled;

      if (innerLeft && innerRight) {
        leftDisabled = innerLeft.classList.contains('arrow--disabled');
        rightDisabled = innerRight.classList.contains('arrow--disabled');
      } else {
        // Respaldo si el plugin `navigation` no está disponible
        const d = this.slider.track && this.slider.track.details;
        if (!d) return;
        leftDisabled = d.abs <= d.minIdx;
        rightDisabled = d.abs >= d.maxIdx;
      }

      this.leftArrow.classList.toggle('arrow--disabled', leftDisabled);
      this.rightArrow.classList.toggle('arrow--disabled', rightDisabled);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.sliderEl && this.onSliderClick) {
        this.sliderEl.removeEventListener('click', this.onSliderClick, true);
      }
      if (this.slider && typeof this.slider.destroy === 'function') this.slider.destroy();
    }
  }

  customElements.define('product-carousel', ProductCarousel);
}
